import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './Login.css';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './shared';
import { isAccountCreationEnabled, isPasswordResetEnabled } from '../config/featureFlags'; // Issue #461
import universeLogo from '../assets/Universe_logo_transparent.svg';

// Issue #248: Flag SVG components for language selector
const GermanFlag = ({ className }) => (
  <svg className={className || "language-selector-flag"} viewBox="0 0 5 3" aria-hidden="true">
    <rect width="5" height="1" y="0" fill="#000"/>
    <rect width="5" height="1" y="1" fill="#DD0000"/>
    <rect width="5" height="1" y="2" fill="#FFCC00"/>
  </svg>
);

const EnglishFlag = ({ className }) => (
  <svg className={className || "language-option-flag"} viewBox="0 0 60 30" aria-hidden="true">
    <clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
    <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
    <g clipPath="url(#s)">
      <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
    </g>
  </svg>
);

const Login = ({ onNavigateToRegister, onNavigateToForgotPassword, onLoginSuccess }) => {
  const { t, language, setLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Issue #248: Language selector state
  const [showLanguagePopover, setShowLanguagePopover] = useState(false);
  const languageSelectorRef = useRef(null);

  // Issue #248: Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageSelectorRef.current && !languageSelectorRef.current.contains(event.target)) {
        setShowLanguagePopover(false);
      }
    };

    if (showLanguagePopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguagePopover]);

  // Issue #248: Handle language change
  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    setShowLanguagePopover(false);
  };

  // Issue #344: Pre-fill credentials from URL parameters (for beta test accounts)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    const passwordParam = params.get('password');

    if (emailParam || passwordParam) {
      setFormData(prev => ({
        ...prev,
        ...(emailParam && { email: decodeURIComponent(emailParam) }),
        ...(passwordParam && { password: decodeURIComponent(passwordParam) }),
      }));

      // Clean URL without reloading (remove sensitive password from URL)
      if (passwordParam) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  // Issue #248: Get current language display info
  const isGerman = language === 'Deutsch';
  const CurrentFlag = isGerman ? GermanFlag : EnglishFlag;
  const currentLanguageText = isGerman ? 'Deutsch' : 'English';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email.trim()) {
      setErrors({ email: t('auth.emailRequired') });
      return;
    }

    if (!formData.password) {
      setErrors({ password: t('auth.passwordRequired') });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await axios.post('/api/login', {
        email: formData.email,
        password: formData.password
      });

      if (response.data.success) {
        // Handle successful login
        // Session cookie is automatically set by backend
        console.log('Login successful:', response.data);
        // Issue #378: Suppress the interstitial ad for the very first login
        // of a brand new account. Persist the flag for the whole session so
        // page reloads during the first login still skip the ad; cleared on logout.
        if (response.data.is_first_login === true) {
          try { sessionStorage.setItem('firstLoginInterstitialSuppressed', 'true'); } catch (e) {}
        }
        if (onLoginSuccess) {
          // Issue #18: Pass otp_login flag to redirect to change password page
          onLoginSuccess(response.data.otp_login || false);
        }
      }
    } catch (error) {
      if (error.response && error.response.data) {
        const { message } = error.response.data;
        setErrors({ general: message });
      } else {
        setErrors({ general: t('auth.systemUnavailable') });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return formData.email.trim() && formData.password;
  };

  return (
    <div className="login-container">
      {/* Skip to main content link for keyboard users */}
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent')}
      </a>

      {/* Navigation Bar */}
      <header className="navigation-bar" role="banner">
        <h1 className="page-title">UNIVERSE</h1>

        {/* Issue #248: Language Selector */}
        <div
          ref={languageSelectorRef}
          className="language-selector"
          onClick={() => setShowLanguagePopover(!showLanguagePopover)}
          role="button"
          aria-haspopup="listbox"
          aria-expanded={showLanguagePopover}
          aria-label={t('languageSettings.selectLanguage') || 'Select language'}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowLanguagePopover(!showLanguagePopover);
            }
          }}
        >
          <CurrentFlag className="language-selector-flag" />
          <span className="language-selector-text">{currentLanguageText}</span>
          <span className={`language-selector-arrow ${showLanguagePopover ? 'open' : ''}`} aria-hidden="true">
            ▼
          </span>

          {/* Language Popover */}
          {showLanguagePopover && (
            <div className="language-popover" role="listbox" aria-label={t('languageSettings.selectLanguage') || 'Select language'}>
              {isGerman ? (
                <button
                  className="language-option-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLanguageChange('Englisch');
                  }}
                  role="option"
                  aria-selected={false}
                >
                  <EnglishFlag className="language-option-flag" />
                  <span className="language-option-text">English</span>
                </button>
              ) : (
                <button
                  className="language-option-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLanguageChange('Deutsch');
                  }}
                  role="option"
                  aria-selected={false}
                >
                  <GermanFlag className="language-option-flag" />
                  <span className="language-option-text">Deutsch</span>
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content" className="main-content" role="main">
        {/* Welcome Header */}
        <div className="welcome-header">
          <img src={universeLogo} alt="Universe Logo" className="welcome-logo" />
          <h2 className="welcome-title">{t('auth.welcomeBack')}</h2>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="login-form" aria-label={t('auth.loginForm')}>
          {/* Email Field */}
          <div className="text-field-wrapper">
            <label htmlFor="login-email" className="visually-hidden">
              {t('auth.studentEmailLabel')}
            </label>
            <input
              type="email"
              id="login-email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('auth.studentEmail')}
              className={`text-field-input ${errors.email ? 'error' : ''}`}
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? 'email-error' : undefined}
              required
              autoComplete="email"
            />
            {errors.email && (
              <div id="email-error" className="error-message" role="alert">
                {errors.email}
              </div>
            )}
          </div>

          {/* Password Field */}
          <div className="text-field-wrapper">
            <label htmlFor="login-password" className="visually-hidden">
              {t('auth.password')}
            </label>
            <div className={`text-field-input-wrapper ${errors.password ? 'error' : ''}`}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="login-password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('auth.password')}
                className="text-field-input-full"
                aria-invalid={errors.password ? 'true' : 'false'}
                aria-describedby={errors.password ? 'password-error' : undefined}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className={`visibility-toggle ${showPassword ? 'active' : ''}`}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t('a11y.hidePassword') : t('a11y.showPassword')}
                aria-pressed={showPassword}
              >
                <span aria-hidden="true">👁️</span>
              </button>
            </div>
            {errors.password && (
              <div id="password-error" className="error-message" role="alert">
                {errors.password}
              </div>
            )}
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="error-message general-error" role="alert" aria-live="polite">
              {errors.general}
            </div>
          )}

          {/* Buttons */}
          <div className="button-group">
            {/* Login Button */}
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={!isFormValid() || isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? t('auth.loggingIn') : t('auth.login')}
            </Button>

            {/* Create Account Button - Issue #461: hidden during the beta test */}
            {isAccountCreationEnabled() && (
              <Button
                variant="secondary"
                fullWidth
                onClick={onNavigateToRegister}
              >
                {t('auth.createAccount')}
              </Button>
            )}

            {/* Forgot Password Button - Issue #461: hidden during the beta test */}
            {isPasswordResetEnabled() && (
              <Button
                variant="secondary"
                fullWidth
                onClick={onNavigateToForgotPassword}
              >
                {t('auth.forgotPassword')}
              </Button>
            )}
          </div>
        </form>
      </main>
    </div>
  );
};

export default Login;
